import { Component, computed, contentChildren, inject, input, output } from '@angular/core';
import { OcpTableData } from 'shared';
import { HlmTr } from '@spartan-ng/helm/table';

import { TableColumnConfig } from '../table-header/table-header.interface';
import { TableRowActionsComponent } from './components/table-row-actions/table-row-actions.component';
import { TableRowCellComponent } from './components/table-row-cell/table-row-cell.component';
import { TableRowCheckboxComponent } from './components/table-row-checkbox/table-row-checkbox.component';
import { TableCellDirective } from './directives/table-cell.directive';
import { HighlightInsertRowDirective } from '../../directives/highlight-insert-row/highlight-insert-row.directive';

@Component({
  selector: 'ocp-table-row',
  imports: [
    TableRowActionsComponent,
    TableRowCellComponent,
    TableRowCheckboxComponent,
    HlmTr,
    HighlightInsertRowDirective,
  ],
  template: `
    <tr
      hlmTableRow
      class="table-row"
      [class]="cssClass()"
      [class.table-row__selected]="isSelected()"
      [ocpHighlightInsertRow]="highlightInsert()"
      (click)="rowClick.emit()"
    >
      @for (col of columns(); track col.field ?? $index) {
        @switch (col.type) {
          @case ('checkbox') {
            <ocp-table-row-checkbox [column]="col" [rowId]="rowId()" [disabled]="disabled()" />
          }
          @case ('actions') {
            <ocp-table-row-actions [column]="col" [rowData]="rowData()" [disabled]="disabled()" />
          }
          @default {
            <ocp-table-row-cell
              (click)="colClick.emit(col)"
              [column]="col"
              [rowData]="rowData()"
              [cellTemplate]="getCellTemplate(col.field)"
            />
          }
        }
      }
    </tr>
  `,
  styleUrl: './table-row.component.scss',
})
export class TableRowComponent {
  private readonly tableData = inject(OcpTableData);

  readonly columns = input.required<TableColumnConfig[]>();
  readonly rowData = input.required<unknown>();
  readonly rowId = input.required<string>();
  readonly cssClass = input('');
  readonly highlightInsert = input<string | boolean | null | undefined>(false);
  readonly disabled = input(false);

  readonly rowClick = output<void>();
  readonly colClick = output<TableColumnConfig>();

  protected readonly isSelected = computed(() => this.tableData.isSelected(this.rowId()));

  private readonly cellDirectives = contentChildren(TableCellDirective);

  private readonly cellTemplateMap = computed(() => {
    const map = new Map<string, TableCellDirective['templateRef']>();
    for (const directive of this.cellDirectives()) {
      map.set(directive.ocpTableCell(), directive.templateRef);
    }
    return map;
  });

  getCellTemplate(field: string | undefined) {
    if (!field) return null;
    return this.cellTemplateMap().get(field) ?? null;
  }
}
